import { ServerResponse, WithAuditDetails } from 'data/types';
import { AxiosError } from 'axios';
import { History } from 'history';
import { Dispatch, Action } from 'redux';
import { RootState } from 'data/root.reducers';
import i18n from 'i18n';
import { showSuccessMessage, showError } from 'shared/toaster';
import { ThunkDispatch } from 'redux-thunk';
import * as api from './filters.api';
import { extractErrorMessage } from '../../utils';
import {
  GET_FILTERS_LIST,
  GET_FILTERS,
  CREATE_FILTERS,
  DELETE_FILTERS,
  FiltersActionTypes,
  Filters,
  GO_TO_FILTERS_LIST,
  SELECT_FILTERS,
  SelectFiltersAction,
  DeleteFiltersThunkAction,
  CreateFiltersThunkAction,
  OPEN_FILTERS_CREATE_PAGE,
  CLOSE_FILTERS_CREATE_PAGE,
  OPEN_FILTERS_EDIT_PAGE,
  CLOSE_FILTERS_EDIT_PAGE,
  SET_FILTERS_LOADING,
  GO_TO_FILTERS_VIEW,
  SetFiltersLoadingAction,
} from './filters.types';
import { FILTERS, FILTER_SETTING, SYSTEM_CONFIGURATION_ROUTE } from '../../constants';
import { getSelectedFilters } from './filters.selectors';

export function goToFiltersList(history: History): (dispatch: Dispatch) => void {
  return (dispatch: Dispatch) => {
    dispatch({
      type: GO_TO_FILTERS_LIST,
    });

    history.push(`${SYSTEM_CONFIGURATION_ROUTE}${FILTER_SETTING}${FILTERS}`);
  };
}

export function goToFiltersCreate(history: History): (dispatch: Dispatch) => void {
  return (dispatch: Dispatch) => {
    dispatch({
      type: OPEN_FILTERS_CREATE_PAGE,
    });

    history.push(`${SYSTEM_CONFIGURATION_ROUTE}${FILTER_SETTING}${FILTERS}/create`);
  };
}

export function closeFiltersCreatePage(history: History): (dispatch: Dispatch) => void {
  return (dispatch: Dispatch) => {
    dispatch({
      type: CLOSE_FILTERS_CREATE_PAGE,
    });

    history.push(`${SYSTEM_CONFIGURATION_ROUTE}${FILTER_SETTING}${FILTERS}`);
  };
}

export function goToFiltersEdit(history: History): (dispatch: Dispatch) => void {
  return (dispatch: Dispatch) => {
    dispatch({
      type: OPEN_FILTERS_EDIT_PAGE,
    });

    history.push(`${SYSTEM_CONFIGURATION_ROUTE}${FILTER_SETTING}${FILTERS}/edit`);
  };
}

export function goToFiltersView(history: History): (dispatch: Dispatch) => void {
  return (dispatch: Dispatch) => {
    dispatch({
      type: GO_TO_FILTERS_VIEW,
    });

    history.push(`${SYSTEM_CONFIGURATION_ROUTE}${FILTER_SETTING}${FILTERS}/view`);
  };
}

export function closeFiltersEditPage(history: History): (dispatch: Dispatch) => void {
  return (dispatch: Dispatch) => {
    dispatch({
      type: CLOSE_FILTERS_EDIT_PAGE,
    });

    history.push(`${SYSTEM_CONFIGURATION_ROUTE}${FILTER_SETTING}${FILTERS}`);
  };
}

export function setFiltersLoadingAction(isLoading: boolean): SetFiltersLoadingAction {
  return {
    type: SET_FILTERS_LOADING,
    payload: { isLoading },
  };
}

export function getFiltersList(): FiltersActionTypes {
  return {
    type: GET_FILTERS_LIST,
    payload: api.getFiltersList(),
  };
}

export function getFilters(id: string): FiltersActionTypes {
  return {
    type: GET_FILTERS,
    payload: api.getFilters(id),
  };
}

export function createFilters(
  deliveryFunctionId: string,
  body: WithAuditDetails<Filters>,
  history: History,
): CreateFiltersThunkAction {
  return async (dispatch: ThunkDispatch<RootState, unknown, Action<string>>) => {
    try {
      await dispatch({
        type: CREATE_FILTERS,
        payload: api.createFilters(deliveryFunctionId, body),
      });

      dispatch(getFiltersList());
      dispatch(goToFiltersList(history));
      // TODO: This must be reenable when the LEA page layout get refactored to fit th proposd UI
      // dispatch(goToFiltersList(history));
      showSuccessMessage(i18n.t('filters.create.success'), 5000);

    } catch (e) {
      const errorMessage = extractErrorMessage(e as AxiosError<ServerResponse<void>>);
      showError(i18n.t('filters.create.failed', { name: body.data?.fId, error: errorMessage }));
    }
  };
}

export function deleteFilters(
  id: string,
  body: WithAuditDetails<void>,
  history?: History,
): DeleteFiltersThunkAction {
  return async (dispatch: ThunkDispatch<RootState, unknown, Action<string>>, getState: () => RootState) => {
    const [selectedFilters] = getSelectedFilters(getState());

    try {
      await dispatch({
        type: DELETE_FILTERS,
        payload: api.deleteFilters(id, body),
      });

      showSuccessMessage(i18n.t('filter.delete.success', { name: selectedFilters?.fId }), 5000);
      if (history != null) {
        dispatch(goToFiltersList(history));
      }
    } catch (e) {
      const errorMessage = extractErrorMessage(e as AxiosError<ServerResponse<void>>);
      showError(i18n.t('filter.delete.failed', { name: selectedFilters?.fId, error: errorMessage }));

    } finally {
      dispatch(getFiltersList());
    }
  };
}

export function selectFilters(
  collectionFunction: Filters[],
): SelectFiltersAction {
  return {
    type: SELECT_FILTERS,
    payload: collectionFunction,
  };
}
